import videojs from 'video.js';
import Quality from './quality.js';
import {
  getMediaLevels,
  checkMasterPlaylist,
  skipFirst
} from './utils.js';

/**
 *
 * @extends EventTarget
 */
export class NativeQualityHandler extends videojs.EventTarget {
	constructor(player, options) {
		super();
		this.player = player;
		this.options = options;
		this.playerSrcBeforeUpdate;
		this.currentMasterPlaylistUrl;
		this.handleQualityChange = skipFirst(this.handleQualityChange.bind(this));
	}

	update() {
		const playerSrc = this.player.src();
		if (this.playerSrcBeforeUpdate === playerSrc) return;
		this.playerSrcBeforeUpdate = playerSrc;

		const playlistRequest = new XMLHttpRequest();
		playlistRequest.addEventListener('load', () => {
			if (!checkMasterPlaylist(playlistRequest.responseText)) return;
			if (this.currentMasterPlaylistUrl === playerSrc) return;
			this.currentMasterPlaylistUrl = playerSrc;
			this.options.qualities.off('change_selected', this.handleQualityChange);
			this.options.qualities.on('change_selected', this.handleQualityChange);
			this.setQualities(playlistRequest.responseText);
		});
		playlistRequest.open('GET', playerSrc);
		playlistRequest.send();
	}

	handleQualityChange() {
		const { representation } = this.options.qualities.selectedQuality;

		const {
			currentTime,
			currentType,
			volume,
			paused
		} = this.getPlayerState();

		this.player.src({
			/* Если есть representation значит выбрано конкретное качество.
			 * Иначе выбрано auto.
			 */
			src: representation
				? representation.url[0]
				: this.currentMasterPlaylistUrl,
			type: currentType
		});
		this.player.one('canplay', () => {
			this.options.changeCurrentQuality(representation);
			this.player.currentTime(
				typeof currentTime === 'function' ? currentTime() : currentTime
			);
			if (paused) {
				this.player.pause();
			}
		});
		this.player.load();
		this.player.play();
		this.player.volume(volume);
	}

	setQualities(playlist) {
		this.options.qualities.clear();

		for (const level of getMediaLevels(playlist)) {
			this.options.qualities.addQuality(new Quality(level));
		}

		let quality;
		for (let _quality of this.options.qualities) {
			if (!_quality.auto) continue;
			quality = _quality;
			break;
		}

		if (this.options.remember_quality) {
			quality = this.options.qualities.getUserPreferredQuality() || quality;
		} else {
			this.options.qualities.clearAutoQuality();
		}

		if (quality) {
			this.options.qualities.selectQuality(quality);
		}
		this.trigger('qualities_initialized');
	}

	getPlayerState() {
		function checkNotNull(value) {
			return value !== null;
		}

		const {
			currentTime: currentTimeFromAudiotracks,
			currentType: currentTypeFromAudiotracks,
			volume: volumeFromAudiotracks,
			paused: pausedFromAudiotracks
		} = this.player.audiotracks().state.playerDataBeforeChangeSrc;
		this.player.audiotracks().resetSavedPlayerState();

		let currentTimeFromMulticam = null;
		let currentTypeFromMulticam = null;
		let volumeFromMulticam = null;
		let pausedFromMulticam = null;
		if (this.player.usingPlugin('multicam')) {
			const {
				currentTime,
				currentType,
				volume,
				paused
			} = this.player.multicam().state.playerDataBeforeChangeSrc;
			currentTimeFromMulticam = currentTime;
			currentTypeFromMulticam = currentType;
			volumeFromMulticam = volume;
			pausedFromMulticam = paused;
			this.player.multicam().resetSavedPlayerState();
		}

		const currentTime = [
			currentTimeFromAudiotracks,
			currentTimeFromMulticam,
			this.player.currentTime()
		].find(checkNotNull);

		const currentType = [
			currentTypeFromAudiotracks,
			currentTypeFromMulticam,
			this.player.currentType()
		].find(checkNotNull);

		const volume = [
			volumeFromAudiotracks,
			volumeFromMulticam,
			this.player.volume()
		].find(checkNotNull);

		const paused = [
			pausedFromAudiotracks,
			pausedFromMulticam,
			this.player.paused()
		].find(checkNotNull);

		return {
			currentTime,
			currentType,
			volume,
			paused
		};
	}
}
